(() => {
  // Cross-page reviews links use ?goto=reviews instead of a hash: a native hash
  // jump lands early and drifts as deferred content above the anchor loads in.
  // This scrolls after load and re-pins while the layout settles, backing off
  // the moment the visitor scrolls on their own.
  const wantsReviews = /[?&]goto=reviews\b/.test(location.search) || location.hash === '#fulfilled-reviews-all';
  if (wantsReviews) {
    let cancelled = false;
    ['wheel', 'touchstart', 'keydown'].forEach((evt) =>
      addEventListener(evt, () => { cancelled = true; }, { passive: true, once: true })
    );

    const pin = () => {
      const el = document.getElementById('fulfilled-reviews-all');
      if (!el) return false;
      const top = Math.max(0, el.getBoundingClientRect().top + scrollY - 12);
      if (Math.abs(scrollY - top) > 8) scrollTo({ top, behavior: 'auto' });
      return true;
    };

    const start = () => {
      if (cancelled) return;
      pin();
      let ticks = 0;
      const iv = setInterval(() => {
        ticks += 1;
        if (cancelled || ticks > 16 || !pin()) clearInterval(iv);
      }, 300);
    };

    if (document.readyState === 'complete') start();
    else addEventListener('load', () => setTimeout(start, 100));
  }

  if (!customElements.get('pdp-variant-radios')) {
    customElements.define('pdp-variant-radios', class PdpVariantRadios extends VariantRadios {
      get viewParam() {
        return this.dataset.pdpView ? `&view=${this.dataset.pdpView}` : '';
      }

      updateMedia() {
        if (!this.currentVariant || !this.currentVariant.featured_media) return;
        const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
        if (!mediaGallery || typeof mediaGallery.setActiveMedia !== 'function') return;
        mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);
      }

      renderProductInfo() {
        const requestedVariantId = this.currentVariant.id;
        fetch(`${this.dataset.url}?variant=${requestedVariantId}${this.viewParam}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            if (this.currentVariant.id !== requestedVariantId) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
            publish(PUB_SUB_EVENTS.variantChange, {
              data: { sectionId: this.dataset.section, html, variant: this.currentVariant }
            });
          });
      }
    });
  }

  if (!customElements.get('pdp-variant-picker')) {
    customElements.define('pdp-variant-picker', class PdpVariantPicker extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', this.onSupplyChange.bind(this));
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.selectDefaultVariant());
        } else {
          this.selectDefaultVariant();
        }
      }

      get selectedCard() {
        const checkedRadio = this.querySelector('input[name="Supply Size"]:checked');
        return checkedRadio ? checkedRadio.closest('[data-pdp-card]') : this.querySelector('[data-pdp-card]');
      }

      onSupplyChange() {
        this.querySelectorAll('[data-pdp-card]').forEach((card) => {
          card.classList.toggle('pdp-vp-card--selected', card.querySelector('input').checked);
        });
      }

      selectDefaultVariant() {
        const card = this.querySelector(`[data-pdp-variant-id="${this.dataset.pdpDefaultVariantId}"]`);
        const radio = card && card.querySelector('input');
        if (!radio || radio.disabled) return;

        if (!radio.checked) card.click();
        this.updateStayAiVariant(this.dataset.pdpDefaultVariantId);
      }

      updateStayAiVariant(variantId) {
        this.stayAiBuyBoxes().forEach((buyBox) => {
          buyBox.variantId = variantId;
          buyBox.updateSellingPlan();
        });
      }

      updateStayAiPlanCache(planId) {
        this.stayAiBuyBoxes().forEach((buyBox) => {
          buyBox.cachedSellingPlanId = planId;
        });
      }

      stayAiBuyBoxes() {
        if (!window.retextionBuyBox) return [];
        return Object.keys(window.retextionBuyBox)
          .filter((key) => key.startsWith(this.dataset.pdpBuyboxPrefix))
          .map((key) => window.retextionBuyBox[key])
          .filter((buyBox) => typeof buyBox.updateSellingPlan === 'function');
      }
    });
  }

  if (!customElements.get('pdp-buy-buttons')) {
    customElements.define('pdp-buy-buttons', class PdpBuyButtons extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          oneTimePrice: this.querySelector('[data-pdp-ot-price-el]'),
          subscriptionPrice: this.querySelector('[data-pdp-sub-price-el]'),
          subscriptionStrike: this.querySelector('[data-pdp-sub-strike-el]'),
          subscriptionFrequency: this.querySelector('[data-pdp-sub-freq-el]'),
          ctaLabel: this.querySelector('[data-pdp-cta-label]'),
          modeButtons: this.querySelectorAll('.pdp-bb-option'),
          submitButton: this.querySelector('[name="add"]')
        };
        this.ctaLabelText = this.elements.ctaLabel.textContent;
        this.sectionId = this.querySelector('form').getAttribute('id').replace('product-form-', '');

        this.elements.modeButtons.forEach((button) => {
          button.addEventListener('click', () => this.setPurchaseMode(button.dataset.pdpMode));
        });
        subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));

        this.syncSellingPlan();
      }

      get picker() {
        return document.querySelector('pdp-variant-picker');
      }

      get isOneTimeMode() {
        return document.body.classList.contains('v2bb-mode-ot');
      }

      onVariantChange(event) {
        if (event.data && event.data.sectionId && event.data.sectionId !== this.sectionId) return;
        const variant = event.data && event.data.variant;
        this.renderSubmitButton(variant);
        if (!variant || !this.picker) return;
        this.picker.querySelector('pdp-variant-radios')?.updateMedia();
        this.picker.updateStayAiVariant(String(variant.id));
        const card = this.picker.querySelector(`[data-pdp-variant-id="${variant.id}"]`);
        if (!card) return;
        this.renderPrices(card);
        if (!this.isOneTimeMode) this.syncSellingPlan();
      }

      renderSubmitButton(variant) {
        if (!variant || variant.available) {
          this.elements.submitButton.removeAttribute('disabled');
          this.elements.ctaLabel.textContent = this.ctaLabelText;
          return;
        }
        this.elements.submitButton.setAttribute('disabled', 'disabled');
        this.elements.ctaLabel.textContent = window.variantStrings ? window.variantStrings.soldOut : 'Sold out';
      }

      renderPrices(card) {
        const { pdpOtPrice, pdpSubPrice, pdpSubStrike, pdpSubFreq } = card.dataset;
        this.elements.oneTimePrice.textContent = pdpOtPrice;
        this.elements.subscriptionPrice.textContent = pdpSubPrice;
        this.elements.subscriptionStrike.textContent = pdpSubStrike === pdpSubPrice ? '' : pdpSubStrike;
        this.elements.subscriptionFrequency.textContent = pdpSubFreq;
      }

      setPurchaseMode(mode) {
        const isOneTime = mode === 'ot';
        document.body.classList.toggle('v2bb-mode-ot', isOneTime);
        this.elements.modeButtons.forEach((button) => {
          const isActive = (button.dataset.pdpMode === 'ot') === isOneTime;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-checked', isActive);
        });

        const stayAiRadio = document.getElementById(isOneTime ? 'purchaseTypeOneTime' : 'purchaseTypeSubscription');
        if (stayAiRadio && !stayAiRadio.checked) {
          stayAiRadio.checked = true;
          stayAiRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (!isOneTime) this.syncSellingPlan();
      }

      syncSellingPlan() {
        const card = this.picker && this.picker.selectedCard;
        if (!card) return;
        this.sellingPlanObserver?.disconnect();
        const applyPlan = () => {
          const dropdown = document.getElementById('rtx-dropdown');
          const plan = dropdown && Array.from(dropdown.options).find((option) =>
            option.textContent.includes(`${card.dataset.pdpFreq} month`)
          );
          if (!plan) return;
          this.picker.updateStayAiPlanCache(plan.value);
          if (dropdown.value === plan.value) return;
          dropdown.value = plan.value;
          dropdown.dispatchEvent(new Event('change', { bubbles: true }));
        };
        applyPlan();
        const dropdown = document.getElementById('rtx-dropdown');
        this.sellingPlanObserver = new MutationObserver(applyPlan);
        if (dropdown) {
          this.sellingPlanObserver.observe(dropdown, { childList: true });
        } else {
          this.sellingPlanObserver.observe(document.body, { childList: true, subtree: true });
        }
      }
    });
  }

  if (!customElements.get('pdp-sticky-bar')) {
    customElements.define('pdp-sticky-bar', class PdpStickyBar extends HTMLElement {
      connectedCallback() {
        if (this.parentElement !== document.body) {
          document.querySelectorAll('pdp-sticky-bar').forEach((bar) => { if (bar !== this) bar.remove(); });
          document.body.appendChild(this);
          return;
        }
        if (this.initialized) return;
        this.initialized = true;
        this.picker = document.querySelector('pdp-variant-picker');
        this.buyButtons = document.querySelector('pdp-buy-buttons');
        if (!this.picker || !this.buyButtons) return;

        this.elements = {
          pills: this.querySelector('[data-pdp-sticky-pills]'),
          price: this.querySelector('[data-pdp-sticky-price]'),
          meal: this.querySelector('[data-pdp-sticky-meal]'),
          mode: this.querySelector('[data-pdp-sticky-mode]'),
          thumb: this.querySelector('.pdp-sticky__thumb'),
          cta: this.querySelector('[data-pdp-sticky-cta]')
        };
        this.mainSubmit = this.buyButtons.querySelector('[name="add"]');
        this.footer = document.querySelector('footer.footer, footer');
        this.ctaPassed = false;
        this.footerVisible = false;

        this.renderPills();
        this.picker.addEventListener('change', () => this.sync());
        subscribe(PUB_SUB_EVENTS.variantChange, () => this.sync());
        new MutationObserver(() => this.sync()).observe(document.body, { attributes: true, attributeFilter: ['class'] });
        this.elements.cta.addEventListener('click', () => this.addToCart());
        this.observeVisibility();
        this.sync();
      }

      get cards() {
        return Array.from(this.picker.querySelectorAll('[data-pdp-card]'));
      }

      get isOneTimeMode() {
        return document.body.classList.contains('v2bb-mode-ot');
      }

      renderPills() {
        this.elements.pills.innerHTML = '';
        this.cards.forEach((card) => {
          const count = card.querySelector('.pdp-vp-card__count');
          const pills = count ? parseInt(count.textContent, 10) : card.dataset.pdpFreq * 30;
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'pdp-sticky__pill';
          pill.dataset.pdpStickyVariant = card.dataset.pdpVariantId;
          pill.innerHTML = `${pills}<small>ct</small>`;
          pill.disabled = card.classList.contains('pdp-vp-card--unavailable');
          pill.addEventListener('click', () => this.selectCard(card));
          this.elements.pills.appendChild(pill);
        });
      }

      selectCard(card) {
        const radio = card.querySelector('input');
        if (!radio || radio.disabled || radio.checked) return;
        radio.click();
      }

      sync() {
        const card = this.picker.selectedCard;
        if (!card) return;
        const oneTime = this.isOneTimeMode;
        this.elements.pills.querySelectorAll('.pdp-sticky__pill').forEach((pill) => {
          const selected = pill.dataset.pdpStickyVariant === card.dataset.pdpVariantId;
          pill.classList.toggle('is-selected', selected);
          pill.setAttribute('aria-pressed', selected);
        });
        this.elements.price.textContent = oneTime ? card.dataset.pdpOtPrice : card.dataset.pdpSubPrice;
        const mealPrice = card.querySelector(oneTime ? '.pdp-vp-card__meal-price--ot' : '.pdp-vp-card__meal-price--sub');
        const mealUnit = card.querySelector('.pdp-vp-card__meal-unit');
        this.elements.meal.textContent = mealPrice && mealUnit ? `${mealPrice.textContent.trim()} ${mealUnit.textContent.trim()}` : '';
        this.elements.mode.textContent = this.modeText(card, oneTime);
        this.renderThumb(card.dataset.pdpVariantId);
        this.elements.cta.disabled = card.classList.contains('pdp-vp-card--unavailable');
      }

      renderThumb(variantId) {
        if (!this.elements.thumb) return;
        const variant = this.variants.find((item) => String(item.id) === String(variantId));
        const image = variant && variant.featured_image && variant.featured_image.src;
        if (!image) return;
        const src = image.replace(/(\?|&)width=\d+/, '') + (image.includes('?') ? '&' : '?') + 'width=160';
        if (this.elements.thumb.getAttribute('src') !== src) this.elements.thumb.src = src;
      }

      modeText(card, oneTime) {
        const option = this.buyButtons.querySelector(oneTime ? '.pdp-bb-option--ot .pdp-bb-option__name' : '.pdp-bb-option--sub .pdp-bb-option__name');
        const name = option ? option.textContent.trim() : '';
        return oneTime ? name : `${name} · ${card.dataset.pdpSubFreq}`;
      }

      get variants() {
        if (!this.variantData) {
          const json = this.picker.querySelector('script[type="application/json"]');
          this.variantData = json ? JSON.parse(json.textContent) : [];
        }
        return this.variantData;
      }

      formIsConsistent() {
        if (this.mainSubmit.disabled || this.mainSubmit.getAttribute('aria-disabled') === 'true') return false;
        const form = this.mainSubmit.form;
        const variantId = form.querySelector('[name="id"]').value;
        const card = this.picker.selectedCard;
        if (!card || card.dataset.pdpVariantId !== variantId) return false;
        if (this.isOneTimeMode) return true;
        const planInput = form.querySelector('[name="selling_plan"]');
        const planId = planInput ? Number(planInput.value) : 0;
        const variant = this.variants.find((item) => String(item.id) === variantId);
        return !!planId && !!variant && (variant.selling_plan_allocations || []).some((allocation) => allocation.selling_plan_id === planId);
      }

      addToCart() {
        if (!this.mainSubmit || this.pendingAdd) return;
        this.pendingAdd = true;
        this.setBusy(true);
        const deadline = Date.now() + 3000;
        const attempt = () => {
          if (this.formIsConsistent()) {
            this.pendingAdd = false;
            this.mainSubmit.click();
            return;
          }
          if (Date.now() > deadline) {
            this.pendingAdd = false;
            this.setBusy(false);
            return;
          }
          setTimeout(attempt, 100);
        };
        attempt();
      }

      setBusy(busy) {
        this.elements.cta.classList.toggle('loading', busy);
        const spinner = this.elements.cta.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.toggle('hidden', !busy);
      }

      observeVisibility() {
        if (!this.mainSubmit) return;
        let scheduled = false;
        const measure = () => {
          scheduled = false;
          this.ctaPassed = this.mainSubmit.getBoundingClientRect().bottom < 0;
          this.footerVisible = !!this.footer && this.footer.getBoundingClientRect().top < window.innerHeight;
          this.updateVisibility();
        };
        const schedule = () => {
          if (scheduled) return;
          scheduled = true;
          window.requestAnimationFrame(measure);
        };
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule, { passive: true });
        measure();
      }

      updateVisibility() {
        const visible = this.ctaPassed && !this.footerVisible;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        document.body.classList.toggle('pdp-sticky-visible', visible);
        const panel = this.querySelector('.pdp-sticky__panel');
        this.liftChatWidget(visible && panel ? panel.offsetHeight : 0);
      }

      liftChatWidget(offset) {
        document.querySelectorAll('#gorgias-facade, #gorgias-chat-container').forEach((widget) => {
          widget.style.transition = 'transform 0.28s ease';
          widget.style.transform = offset ? `translateY(-${offset}px)` : '';
        });
      }
    });
  }
})();
